import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { getAxiosApiV2WithAuthorization, captureError } from '@clatter/platform';
import { config } from '../config';
import { baseApi } from '../services/baseApi';
import { addLongPullUIItem, updateLongPullUIItem } from '../store';
import { useCreateUploadRequestMutation, useMarkUploadStatusCompletedMutation } from '../services/upload.service';
import axios from 'axios';

type BatchId = string;

type BatchState = {
  id: BatchId;
  notificationItemId: string;
  folderId: number;
  total: number;
  completed: number;
};

type RequestMeta = {
  batchId: BatchId;
  fileName: string;
  folderId: number;
};

type TerminalStatus = 'success' | 'failed' | 'timeout';

type UploadStatus = {
  Status?: TerminalStatus | 'pending' | 'processing' | string;
  status?: TerminalStatus | 'pending' | 'processing' | string; // some APIs use lowercase
  ErrorMessage?: string | null;
  data?: any; // For slide repository responses
};

export const useUpload = () => {
  const dispatch = useDispatch();

  const [createUploadRequest] = useCreateUploadRequestMutation();
  const [markUploadStatusCompleted] = useMarkUploadStatusCompletedMutation();

  // removed global in-flight dedupe; only dedupe within a single batch
  const activeBatchRef = useRef<Set<string>>(new Set());
  const batchesRef = useRef<Map<BatchId, BatchState>>(new Map());
  const requestToBatchRef = useRef<Map<number, RequestMeta>>(new Map());
  const pollingTimersRef = useRef<Map<number, number>>(new Map());
  const statusLocationRef = useRef<Map<number, string>>(new Map());

  const fileKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
  const toList = (fl?: FileList | null) => (fl && fl.length ? Array.from(fl) : []);

  const clearPollingFor = (requestId: number) => {
    const handle = pollingTimersRef.current.get(requestId);
    if (handle) {
      clearInterval(handle);
      pollingTimersRef.current.delete(requestId);
    }
    //statusLocationRef.current.delete(requestId);
  };

  const finishFileInBatch = useCallback(
    async (requestId: number, status: TerminalStatus, errorMsg?: string) => {
      const meta = requestToBatchRef.current.get(requestId);
      if (!meta) {
        // still stop polling even with no bookkeeping left — a terminal call means we're done either way
        clearPollingFor(requestId);
        return;
      }

      const batch = batchesRef.current.get(meta.batchId);
      if (!batch) {
        requestToBatchRef.current.delete(requestId);
        clearPollingFor(requestId);
        return;
      }

      const nextCompleted = batch.completed + 1;
      const isDone = nextCompleted >= batch.total;

      const perFileMsg =
        status === 'success'
          ? `Successfully uploaded file "${meta.fileName}".`
          : `Error uploading file "${meta.fileName}": ${errorMsg || 'Unknown error'}`;

      await dispatch(
        updateLongPullUIItem({
          itemId: batch.notificationItemId,
          updates: {
            // only use the generic "Finished" summary on success — otherwise the real error would get swallowed
            message:
              isDone && status === 'success'
                ? `Finished ${nextCompleted}/${batch.total} files.`
                : batch.total > 1
                ? `${perFileMsg} (${nextCompleted}/${batch.total})`
                : perFileMsg,
            type: status === 'success' ? 'success' : 'error',
            apiStatus: status, // Set the API status (success/failed/timeout)
            completedFiles: nextCompleted,
            ...(isDone ? { isCompleted: true, isUploading: false } : {}),
          },
        }),
      );

      // invalidate rtk caches
      await dispatch(baseApi.custom.invalidateAllTags());

      // update internal batch counters / cleanup
      batch.completed = nextCompleted;

      if (isDone) {
        batchesRef.current.delete(meta.batchId);
      }

      requestToBatchRef.current.delete(requestId);

      clearPollingFor(requestId);
    },
    [dispatch],
  );

  const startPollingStatus = useCallback(
    (requestId: number) => {
      clearPollingFor(requestId);

      const interval = window.setInterval(async () => {
        try {
          // Use IngestStatusLocation if present, otherwise fall back to default endpoint
          const statusUrl = statusLocationRef.current.get(requestId) || `${config.assetServiceBaseUrl}/upload-requests/${requestId}`;

          const response = await getAxiosApiV2WithAuthorization({
            url: statusUrl,
          });

          const data: UploadStatus | undefined = response?.data;
          // Check both nested (data.Status/data.status) and root level (response.status)
          const status = (data?.Status || data?.status || response?.status || '').toLowerCase();

          // Update Redux with the latest polling data
          const meta = requestToBatchRef.current.get(requestId);
          if (meta) {
            const batch = batchesRef.current.get(meta.batchId);
            if (batch) {
              await dispatch(
                updateLongPullUIItem({
                  itemId: batch.notificationItemId,
                  updates: {
                    apiStatus: data?.Status || data?.status || response?.status, // Store the API status (check both nested and root)
                    responseData: data, // Store the full polling response
                  },
                }),
              );
            }
          }

          if (status === 'success' || status === 'failed' || status === 'timeout') {
            await finishFileInBatch(requestId, status as TerminalStatus, data?.ErrorMessage || undefined);
          }
        } catch (e: any) {
          captureError(e, { message: 'Error polling upload status', component: 'useUpload' });
          // stop polling and notify user on error
          const errorMessage = e?.response?.data?.ErrorMessage || e?.response?.data?.error || e?.message || 'Failed to check upload status';
          await finishFileInBatch(requestId, 'failed', errorMessage);
        }
      }, config.defaultFetchInterval);

      pollingTimersRef.current.set(requestId, interval);
    },
    [finishFileInBatch, dispatch],
  );

  const uploadFiles = useCallback(
    async ({
      file: inputFile,
      files,
      folderId,
      asset_id,
      isInlineProgress,
    }: {
      file?: File;
      files?: FileList;
      asset_id?: number;
      folderId: number;
      isInlineProgress?: boolean;
    }) => {
      // track duplicates only within this batch (same drop)
      const batchSeen = new Set<string>();

      // normalize to list
      const batchFiles: File[] = inputFile ? [inputFile] : toList(files);

      if (!batchFiles.length) {
        captureError(new Error('Error uploading file(s). No data provided!'), { component: 'useUpload', skipConsole: true });
        return;
      }

      // prevent starting the exact same batch twice at once
      const batchSignature = `${folderId}|${asset_id ?? 'na'}|` + batchFiles.map(fileKey).join('||');
      if (activeBatchRef.current.has(batchSignature)) return;
      activeBatchRef.current.add(batchSignature);

      // create batch + long-pull UI item
      const batchId: BatchId = uuidv4();
      const longPullItemId = uuidv4();

      batchesRef.current.set(batchId, {
        id: batchId,
        notificationItemId: longPullItemId,
        folderId,
        total: batchFiles.length,
        completed: 0,
      });

      await dispatch(
        addLongPullUIItem({
          item: {
            id: longPullItemId,
            isUploading: true,
            isCompleted: false,
            completedFiles: 0,
            totalFiles: batchFiles.length,
            message:
              batchFiles.length === 1
                ? `Starting upload for "${batchFiles[0].name}".`
                : `Starting upload of ${batchFiles.length} files...`,
            type: 'pending',
            isFileUpload: true,
            isInlineProgress,
            apiStatus: undefined, // API processing status
            responseData: undefined, // Initialize so it can be updated later
          },
        }),
      );

      let lastResult: any = null;
      let lastUploadRequestId: number | undefined;

      // Return notification ID immediately so caller can track progress
      const uploadInfo = {
        notificationItemId: longPullItemId,
        batchId: batchId,
      };

      try {
        for (const f of batchFiles) {
          const k = fileKey(f);

          // skip exact duplicates within the same dropped list
          if (batchSeen.has(k)) {
            continue;
          }
          batchSeen.add(k);

          try {
            // 1) create upload request
            const payload: any = {
              file_name: f.name,
              file_type: f.type,
              file_size: f.size,
              folder_id: folderId,
            };
            if (asset_id) payload['asset_id'] = asset_id;

            const uploadRequestResponse = await createUploadRequest({
              data: payload,
            }).unwrap();

            if (!uploadRequestResponse || !uploadRequestResponse.ID) {
              throw new Error('Failed to create upload request - invalid response');
            }

            const {
              PresignedUrl: presignedUrl,
              ID: uploadRequestId,
              IngestStatusLocation,
              ShouldNotifyUploadCompleted
            } = uploadRequestResponse;

            lastUploadRequestId = uploadRequestId;

            // Store the status location if provided for polling
            if (IngestStatusLocation) {
              statusLocationRef.current.set(uploadRequestId, IngestStatusLocation);
            }

            // 2) put file to storage
            await axios.put(presignedUrl, f, {
              headers: {
                'Content-Type': f.type || 'application/octet-stream',
              },
            });

            // 3) mark as completed (server begins processing)
            // Skip if ShouldNotifyUploadCompleted is true (server handles completion notification)
            let markResponse;
            if (ShouldNotifyUploadCompleted !== false) {
              markResponse = await markUploadStatusCompleted({ requestId: uploadRequestId }).unwrap();
            }

            // 4) update UI (storage step done; server processing...)
            const batch = batchesRef.current.get(batchId);
            const message = `Uploaded to storage: "${f.name}". Processing on server... (${batch?.completed ?? 0}/${
              batch?.total ?? batchFiles.length
            })`;

            await dispatch(
              updateLongPullUIItem({
                itemId: longPullItemId,
                updates: { message },
              }),
            );

            // 5) Start per-file polling and map request->batch/file
            requestToBatchRef.current.set(uploadRequestId, {
              batchId,
              fileName: f.name,
              folderId,
            });
            startPollingStatus(uploadRequestId);

            // best-effort priming fetch — must not fail the upload, polling above will catch up regardless
            let primingResponseData: any;
            try {
              const statusUrl = statusLocationRef.current.get(uploadRequestId) || `${config.assetServiceBaseUrl}/upload-requests/${uploadRequestId}`;

              const response = await getAxiosApiV2WithAuthorization({
                url: statusUrl,
              });

              primingResponseData = response?.data;

              await dispatch(
                updateLongPullUIItem({
                  itemId: longPullItemId,
                  updates: {
                    apiStatus: response?.data?.Status || response?.data?.status || response?.status,
                    responseData: response?.data,
                  },
                }),
              );
            } catch (primingError) {
              captureError(primingError, { message: 'Error priming initial upload status', component: 'useUpload', skipConsole: true });
            }

            lastResult = {
              status: 'success',
              error: null,
              presignedUrl: markResponse?.AssetVersion?.Url,
              responseData: primingResponseData,
            };
          } catch (error: any) {
            // early failure (no polling will occur for this file)
            const batch = batchesRef.current.get(batchId);
            if (batch) {
              batch.completed += 1;
              const isDone = batch.completed >= batch.total;

              const message =
                error?.data?.error === 'Access denied.'
                  ? `Access denied for "${f.name}".`
                  : `Error uploading "${f.name}"${error?.data?.error ? `: ${error?.data?.error}` : ''}`;

              await dispatch(
                updateLongPullUIItem({
                  itemId: batchesRef.current.get(batchId)!.notificationItemId,
                  updates: {
                    // always a failure here, so never use the generic "Finished" success summary
                    message: batch.total > 1 ? `${message} (${batch.completed}/${batch.total})` : message,
                    type: 'error',
                    apiStatus: 'failed',
                    responseData: error?.data || { error: error?.message || 'Upload failed' },
                    completedFiles: batch.completed,
                    ...(isDone ? { isCompleted: true, isUploading: false } : {}),
                  },
                }),
              );

              // invalidate even on failure (in case partial assets got created)
              // await dispatch(baseApi.util.invalidateTags([{ type: 'Folders', id: `ASSETS_${folderId}` }]));
              // await dispatch(baseApi.util.invalidateTags([{ type: 'Assets', id: 'VERSIONS' }]));
              await dispatch(baseApi.custom.invalidateAllTags());

              if (isDone) {
                batchesRef.current.delete(batchId);
              }
            }

            lastResult = { status: 'failed', error };
          }
        }

        // Return both the last result and the upload tracking info
        return {
          ...lastResult,
          ...uploadInfo,
          uploadRequestId: lastUploadRequestId,
        };
      } finally {
        activeBatchRef.current.delete(batchSignature);
      }
    },
    [createUploadRequest, markUploadStatusCompleted, dispatch, startPollingStatus],
  );

  useEffect(() => {
    return () => {
      // don't clear the batch/request maps here — StrictMode fires this mid-flight and wipes state uploadFiles() is still using
      for (const [, handle] of pollingTimersRef.current) {
        clearInterval(handle);
      }
      pollingTimersRef.current.clear();
    };
  }, []);

  return { uploadFiles };
};
